import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { isApolloError } from '@apollo/client';
import { useTitle } from 'ahooks';
import { Button, Col, Form, FormProps, Input, Row, Typography } from 'antd';
import { FC, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { useAuthentication } from '../../../components/AuthenticationProvider';
import useRedirect from '../../../components/AuthenticationProvider/useRedirect';
import AuthException, {
  AuthExceptionMessage,
} from '../../../constants/AuthException';
import { AuthChallenge } from '../../../graphql.generated';
import paths from '../../../paths.json';

import styles from './styles.module.less';

const title = 'Welcome Back';

type FormValues = {
  username: string;
  password: string;
};

const LoginForm: FC<FormProps<FormValues>> = (props) => {
  const history = useHistory();
  const redirect = useRedirect();
  useTitle(title);

  const { login } = useAuthentication();

  const onFinish = useCallback(
    async ({ username, password }: FormValues) => {
      let response;
      try {
        response = await login(username, password);
      } catch (e) {
        let message = 'Critical error during login. Please try again.';
        if (isApolloError(e as Error)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const type = e.graphQLErrors?.[0]?.type as AuthException;
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          message = (type && AuthExceptionMessage[type]) || e.message;
        }

        history.replace(`${paths.login}`, {
          error: message,
        });

        return;
      }

      if (
        response?.data?.login?.authChallenge ===
        AuthChallenge.NewPasswordRequired
      ) {
        history.replace(`${paths.changePassword}`, {
          oldPassword: password,
          username,
        });
      } else {
        redirect();
      }
    },
    [history, login, redirect],
  );

  return (
    <Row data-name="login-page" gutter={[8, 28]}>
      <Col xs={24}>
        <Row align="middle" gutter={[8, 8]} justify="center">
          <Col xs={24}>
            <Typography.Title className={styles.title} level={3}>
              {title}
            </Typography.Title>
          </Col>
          <Col>
            <Typography.Title
              className={styles.title}
              level={5}
              type="secondary"
            >
              Enter your credentials to access your account.
            </Typography.Title>
          </Col>
        </Row>
      </Col>
      <Col xs={24}>
        <Form data-test="login-form" {...props} onFinish={onFinish}>
          <Form.Item name="username" rules={[{ required: true }]}>
            <Input
              placeholder="Email"
              prefix={<MailOutlined />}
              size="large"
              type="email"
            />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true }]}>
            <Input.Password
              placeholder="Password"
              prefix={<LockOutlined />}
              size="large"
            />
          </Form.Item>
          <Row align="middle" gutter={[8, 16]} justify="center">
            <Col xs={24}>
              <Button block htmlType="submit" size="large" type="primary">
                Login
              </Button>
            </Col>
            <Col>
              <Link to={paths.resetPassword}>
                <Button type="link">Forgot Password?</Button>
              </Link>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

LoginForm.displayName = 'LoginForm';

export default LoginForm;
